'use client';

import { c as _c } from "react/compiler-runtime";
import { type Post } from '@shared/all/domains/posts';
import useLocalPreference from '@shared/utils/hooks/useLocalPreference';
import { ChevronLeft, ChevronRight, Download, X } from 'lucide-react';
import Image from 'next/image';
import { useEffect, useReducer } from 'react';
import { downloadImage } from '../../utils/download';
import { getDisplaySrc, getUploadSrc } from '../../utils/uploads';
import Button from '../common/Button';
import DocumentItem from '../common/DocumentItem';
import ModalRaw from '../HOC/ModalRaw';
import VideoPlayer from '../UI/Video';
type Props = {
  closeHandler: () => void;
  data: Post['uploads'];
  initialIndex: number;
  nsfw: boolean;
};

/** Matches the CSS durations below — we unmount only after the exit finishes. */
const EXIT_MS = 180;

// Videos may not carry dimensions (no VIEW variant) — assume landscape.
const FALLBACK_ASPECT = 16 / 9;
type State = {
  index: number;
  blurred: boolean;
  phase: 'open' | 'leaving';
};
type Action = {
  type: 'next';
  max: number;
} | {
  type: 'prev';
} | {
  type: 'reveal';
} | {
  type: 'leave';
};
const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'next':
      return {
        ...state,
        index: Math.min(state.index + 1, action.max)
      };
    case 'prev':
      return {
        ...state,
        index: Math.max(state.index - 1, 0)
      };
    case 'reveal':
      return {
        ...state,
        blurred: false
      };
    case 'leave':
      return {
        ...state,
        phase: 'leaving'
      };
  }
};
const ViewPostMedia = t0 => {
  const $ = _c(65);
  const {
    closeHandler,
    data,
    initialIndex,
    nsfw
  } = t0;
  let t1;
  if ($[0] !== initialIndex || $[1] !== nsfw) {
    t1 = {
      index: initialIndex,
      blurred: nsfw,
      phase: "open"
    };
    $[0] = initialIndex;
    $[1] = nsfw;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const [state, dispatch] = useReducer(reducer, t1);
  const {
    localPreferenceState
  } = useLocalPreference();
  let t2;
  if ($[3] !== closeHandler || $[4] !== state.phase) {
    t2 = () => {
      if (state.phase === "leaving") {
        return;
      }
      dispatch({
        type: "leave"
      });
      setTimeout(closeHandler, EXIT_MS);
    };
    $[3] = closeHandler;
    $[4] = state.phase;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const close = t2;
  let t3;
  if ($[6] !== close || $[7] !== data?.length) {
    t3 = () => {
      const onKey = e => {
        if (e.key === "Escape") {
          e.stopPropagation();
          close();
        }
        if (e.key === "ArrowRight") {
          dispatch({
            type: "next",
            max: (data?.length ?? 1) - 1
          });
        }
        if (e.key === "ArrowLeft") {
          dispatch({
            type: "prev"
          });
        }
      };
      document.addEventListener("keydown", onKey);
      return () => document.removeEventListener("keydown", onKey);
    };
    $[6] = close;
    $[7] = data?.length;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  useEffect(t3);
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [];
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  useEffect(_temp2, t4);
  const currentMedia = data?.[state.index];
  if (!currentMedia) {
    return null;
  }
  const aspectOf = _temp3;
  let t5;
  if ($[10] !== data) {
    t5 = Math.min(...(data ?? []).map(aspectOf));
    $[10] = data;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const narrowest = t5;
  const hasPrev = state.index > 0;
  const hasNext = !!data && state.index < data.length - 1;
  const multiple = !!data && data.length > 1;
  const leaving = state.phase === "leaving";
  const t6 = `fixed inset-0 z-modal bg-black/90 motion-reduce:animate-none ${leaving ? "animate-overlay-out" : "animate-overlay-in"}`;
  let t7;
  if ($[12] !== close) {
    t7 = e_0 => {
      e_0.stopPropagation();
      close();
    };
    $[12] = close;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t6 || $[15] !== t7) {
    t8 = <div className={t6} onClick={t7} />;
    $[14] = t6;
    $[15] = t7;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== close) {
    t9 = e_1 => {
      e_1.stopPropagation();
      close();
    };
    $[17] = close;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const t10 = `relative flex w-[var(--avail-w)] items-center justify-center motion-reduce:animate-none ${leaving ? "animate-panel-out" : "animate-panel-in"}`;
  let t11;
  if ($[19] !== narrowest) {
    t11 = narrowest.toFixed(4);
    $[19] = narrowest;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  const t12 = `min(var(--avail-h), calc(var(--avail-w) / ${t11}))`;
  let t13;
  if ($[21] !== t12) {
    t13 = {
      height: t12
    };
    $[21] = t12;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== currentMedia || $[24] !== state.blurred) {
    t14 = currentMedia.type === "IMAGE" && <Image width={currentMedia.display?.width ?? 800} height={currentMedia.display?.height ?? 600} sizes="100vw" style={{
      width: "100%",
      height: "auto"
    }} className={`max-h-full object-contain sm:rounded-lg ${state.blurred ? "blur-2xl" : ""}`} src={getDisplaySrc(currentMedia)} alt="Posted image" priority={true} aria-hidden={state.blurred} />;
    $[23] = currentMedia;
    $[24] = state.blurred;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== currentMedia || $[27] !== localPreferenceState || $[28] !== state.blurred) {
    t15 = currentMedia.type === "VIDEO" && !state.blurred && <div className="w-full" style={{
      height: `min(100%, calc(var(--avail-w) / ${aspectOf(currentMedia).toFixed(4)}))`
    }}><VideoPlayer key={currentMedia.id} fill={true} src={getUploadSrc(currentMedia)} preload="metadata" controls={true} autoPlay={true} playsInline={true} muted={Boolean(localPreferenceState.muteVideo)} /></div>;
    $[26] = currentMedia;
    $[27] = localPreferenceState;
    $[28] = state.blurred;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== currentMedia) {
    t16 = currentMedia.type === "DOCUMENT" && <div className="flex min-h-40 items-center justify-center"><DocumentItem data={currentMedia} inline={true} /></div>;
    $[30] = currentMedia;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] !== t10 || $[33] !== t13 || $[34] !== t14 || $[35] !== t15 || $[36] !== t16) {
    t17 = <div className={t10} style={t13} onClick={_temp4}>{t14}{t15}{t16}</div>;
    $[32] = t10;
    $[33] = t13;
    $[34] = t14;
    $[35] = t15;
    $[36] = t16;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  let t18;
  if ($[38] !== close || $[39] !== state.blurred) {
    t18 = state.blurred && <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/80 p-4 text-center text-white sm:p-6" onClick={_temp5}><h1 className="text-lg font-semibold sm:text-2xl">This media belongs to an NSFW post.</h1><p className="max-w-sm text-sm">This could potentially contain content that some people find disturbing. Do you still want to view this media?</p><div className="flex flex-wrap justify-center gap-3"><Button text="Go Back" variant="SECONDARY" classNames="w-28 bg-background-secondary hover:bg-background-light" onClick={close} /><Button text="View anyway" classNames="w-28 hover:bg-background-light" onClick={() => dispatch({
          type: "reveal"
        })} /></div></div>;
    $[38] = close;
    $[39] = state.blurred;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  let t19;
  if ($[41] !== currentMedia || $[42] !== state.blurred) {
    t19 = currentMedia.type === "IMAGE" && !state.blurred && <button title="Save image" aria-label="Save image" className="flex size-10 items-center justify-center rounded-full bg-black/60 text-white backdrop-blur transition hover:bg-black/80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white/80 active:scale-95 disabled:pointer-events-none disabled:opacity-30 motion-reduce:transition-none" onClick={() => void downloadImage(getDisplaySrc(currentMedia), currentMedia.isFactiii ?? false, `factiii-${currentMedia.id ?? "image"}.jpg`)}><Download size={18} /></button>;
    $[41] = currentMedia;
    $[42] = state.blurred;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <X size={18} />;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] !== close) {
    t21 = <button title="Close" aria-label="Close" className="flex size-10 items-center justify-center rounded-full bg-black/60 text-white backdrop-blur transition hover:bg-black/80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white/80 active:scale-95 disabled:pointer-events-none disabled:opacity-30 motion-reduce:transition-none" onClick={close}>{t20}</button>;
    $[45] = close;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  let t22;
  if ($[47] !== t19 || $[48] !== t21) {
    t22 = <div className="absolute right-3 top-3 flex items-center gap-2" onClick={_temp6}>{t19}{t21}</div>;
    $[47] = t19;
    $[48] = t21;
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  let t23;
  if ($[50] !== data || $[51] !== hasNext || $[52] !== hasPrev || $[53] !== multiple || $[54] !== state.index) {
    t23 = multiple && <><button title="Previous" aria-label="Previous" disabled={!hasPrev} className="flex size-10 items-center justify-center rounded-full bg-black/60 text-white backdrop-blur transition hover:bg-black/80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white/80 active:scale-95 disabled:pointer-events-none disabled:opacity-30 motion-reduce:transition-none absolute left-2 top-1/2 -translate-y-1/2 sm:left-4" onClick={e_5 => {
        e_5.stopPropagation();
        dispatch({
          type: "prev"
        });
      }}><ChevronLeft size={20} /></button><button title="Next" aria-label="Next" disabled={!hasNext} className="flex size-10 items-center justify-center rounded-full bg-black/60 text-white backdrop-blur transition hover:bg-black/80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-white/80 active:scale-95 disabled:pointer-events-none disabled:opacity-30 motion-reduce:transition-none absolute right-2 top-1/2 -translate-y-1/2 sm:right-4" onClick={e_6 => {
        e_6.stopPropagation();
        dispatch({
          type: "next",
          max: (data?.length ?? 1) - 1
        });
      }}><ChevronRight size={20} /></button><div className="absolute left-3 top-3 rounded-full bg-black/60 px-2.5 py-1.5 text-xs font-medium text-white backdrop-blur">{state.index + 1} / {data.length}</div></>;
    $[50] = data;
    $[51] = hasNext;
    $[52] = hasPrev;
    $[53] = multiple;
    $[54] = state.index;
    $[55] = t23;
  } else {
    t23 = $[55];
  }
  let t24;
  if ($[56] !== t17 || $[57] !== t18 || $[58] !== t22 || $[59] !== t23 || $[60] !== t9) {
    t24 = <div className="fixed inset-0 z-modal flex items-center justify-center [--avail-h:100dvh] [--avail-w:100vw] sm:p-6 sm:[--avail-h:85dvh] sm:[--avail-w:calc(100vw-3rem)]" onClick={t9}>{t17}{t18}{t22}{t23}</div>;
    $[56] = t17;
    $[57] = t18;
    $[58] = t22;
    $[59] = t23;
    $[60] = t9;
    $[61] = t24;
  } else {
    t24 = $[61];
  }
  let t25;
  if ($[62] !== t24 || $[63] !== t8) {
    t25 = <ModalRaw>{t8}{t24}</ModalRaw>;
    $[62] = t24;
    $[63] = t8;
    $[64] = t25;
  } else {
    t25 = $[64];
  }
  return t25;
};
export default ViewPostMedia;
function _temp() {
  return document.body.classList.remove("removeScroller");
}
function _temp2() {
  document.body.classList.add("removeScroller");
  return _temp;
}
function _temp3(u) {
  const w = u.display?.width;
  const h = u.display?.height;
  return w && h ? w / h : FALLBACK_ASPECT;
}
function _temp4(e_2) {
  return e_2.stopPropagation();
}
function _temp5(e_3) {
  return e_3.stopPropagation();
}
function _temp6(e_4) {
  return e_4.stopPropagation();
}